import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_PSYCHOPATH,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { getPsycReportPageImage } from "../../utils/utility-functions";
import Header from "../../components/header";
import BackToTopButton from "../../components/back-to-top/component";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import PathHelper from "../../utils/path-helper";
import {
  calculatePsychopathAssessmentScores,
  getPsychopathCombinationContent,
  getPsychopathLevel,
  getPsychopathOverallLevel,
  getPsychopathTraitScores,
} from "../../utils/assessment-utils";
import ExpandableSectionV2 from "../../components/expandable-collapsible-v2/";
import ReportWrapper from "../../components/report-wrapper";

const SociopathTestReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true);

      try {
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );

        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_PSYCHOPATH[currentLocale] ||
            MODEL_TYPE_PSYCHOPATH[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          setContentData(contentResponse.content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const result1ImageText = "psyc-result-1";
  const result1Image = getPsycReportPageImage(result1ImageText);
  const result2ImageText = "psyc-result-2";
  const result2Image = getPsycReportPageImage(result2ImageText);

  const colorVariants = {
    violet: {
      from: "rgb(106, 35, 201)",
      to: "rgb(178, 75, 214)",
    },
    blue: {
      from: "#26307F",
      to: "#3F63D8",
    },
    green: {
      from: "#146C43",
      to: "#2FAE66",
    },
    red: {
      from: "#C0392B",
      to: "#E0685A",
    },
    orange: {
      from: "#C2410C",
      to: "#F97316",
    },
    teal: {
      from: "#0F766E",
      to: "#14B8A6",
    },
    pink: {
      from: "#BE185D",
      to: "#EC4899",
    },
    indigo: {
      from: "#3730A3",
      to: "#6366F1",
    },
    cyan: {
      from: "#0F766E",
      to: "#06B6D4",
    },
    yellow: {
      from: "#B45309",
      to: "#FACC15",
    },
  };

  const TakeTestButton = ({ onClick, id }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id={id}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const bannerColors = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className="relative h-auto overflow-hidden"
        style={{
          background: `
          radial-gradient(1.6px 1.6px at 40px 26px, rgba(255,255,255,0.40), transparent),
          radial-gradient(1.4px 1.4px at 160px 70px, rgba(255,255,255,0.28), transparent),
          radial-gradient(1.1px 1.1px at 300px 34px, rgba(255,255,255,0.30), transparent),
          radial-gradient(1.5px 1.5px at 460px 84px, rgba(255,255,255,0.25), transparent),
          radial-gradient(1px 1px at 600px 48px, rgba(255,255,255,0.30), transparent),
          radial-gradient(1.2px 1.2px at 760px 32px, rgba(255,255,255,0.25), transparent),
          radial-gradient(1.3px 1.3px at 920px 76px, rgba(255,255,255,0.35), transparent),
          linear-gradient(105deg, ${bannerColors.from}, ${bannerColors.to})
        `,
        }}
      >
        <div className="absolute inset-0 bg-black/10" />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(
            to right,
            ${bannerColors.from}DD 0%,
            ${bannerColors.from}88 35%,
            transparent 75%
          )`,
          }}
        />
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-4 lg:px-8 lg:py-6">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-4 md:pr-16">
              <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                <h2
                  id={reportType}
                  className="text-left text-[27px] font-bold text-white break-words whitespace-pre-wrap"
                >
                  {title}
                </h2>
              </div>
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  const { impulsivityScore, sociopathyScore } =
    getPsychopathTraitScores(userTraits);

  const sociopathyScoreLevel = getPsychopathLevel(sociopathyScore);
  const impulsivityScoreLevel = getPsychopathLevel(impulsivityScore);

  const overAllScore = calculatePsychopathAssessmentScores(
    sociopathyScore,
    impulsivityScore,
  ).overallScore;

  const overAllLevel = getPsychopathOverallLevel(overAllScore);

  const sociopathyDetailedScores = calculatePsychopathAssessmentScores(
    sociopathyScore,
    impulsivityScore,
  );

  const calculatedSociopathyScore =
    sociopathyDetailedScores?.sociopathyScore ?? 0;
  const calculatedImpulsivityScore =
    sociopathyDetailedScores?.impulsivityScore ?? 0;

  const getYourInsightContent = getPsychopathCombinationContent(
    contentData?.personalizedBreakdown?.combinations,
    sociopathyScoreLevel,
    impulsivityScoreLevel,
  );

  const progressBarColor = ["#E0791F", "#4CAF50"];

  const progressScore = userTraits?.map((item, index) => {
    let calculatedPercentage = item?.percentage;
    if (item?.trait?.title === "sociopathy") {
      calculatedPercentage = calculatedSociopathyScore;
    }
    if (item?.trait?.title === "impulsivity") {
      calculatedPercentage = calculatedImpulsivityScore;
    }
    return {
      label: item?.trait?.title,
      percentage: calculatedPercentage,
      color: progressBarColor[index % progressBarColor.length],
    };
  });

  const TIER_STYLES = {
    green: "bg-green-50 border-green-500 text-green-700",
    yellow: "bg-yellow-50 border-yellow-500 text-yellow-700",
    red: "bg-red-50 border-red-500 text-red-700",
    orange:
      "bg-[rgb(253,243,230)] border border-[rgb(243,221,184)] text-[#E0791F] rounded-[14px]",
  };

  const TIER_TEXT = {
    green: "text-green-600",
    yellow: "text-yellow-600",
    red: "text-red-600",
    orange: "text-[#E0791F]",
  };

  const TIER_COLORS = {
    green: {
      stroke: "#37C978",
      text: "text-[#249D5C]",
    },
    yellow: {
      stroke: "#F6C343",
      text: "text-[#D89E00]",
    },
    red: {
      stroke: "#F25555",
      text: "text-[#E53935]",
    },
    orange: {
      stroke: "#E0791F",
      text: "text-[#E0791F]",
    },
  };

  const ResultMeaningCard = ({ title, description, tier = "orange" }) => (
    <div
      className={`${TIER_STYLES[tier]}`}
      style={{ borderRadius: "14px", padding: "20px 22px" }}
    >
      <h4
        className="mb-2"
        style={{
          marginTop: "2px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          fontSize: "19px",
        }}
      >
        {title}
      </h4>
      <p className="text-gray-600 text-[15px] leading-relaxed">{description}</p>
    </div>
  );

  const ScoresTable = ({ rows }) => {
    return (
      <div className="w-full mt-8">
        <div className="grid grid-cols-[1fr_auto] items-center pb-4 border-b border-gray-200">
          <span className="text-[16px] font-bold text-[#111827]">
            {t("report.psychopathy.trait_score_label")}
          </span>
          <span className="text-[16px] font-bold text-[#111827] text-right">
            {t("report.autism_children.results")}
          </span>
        </div>
        {rows.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_auto] items-center py-6 border-b border-gray-200 last:border-b-0"
          >
            <span className="text-[15.5px] text-[#374151]">{row.label}</span>
            <span
              className={`text-[15.5px] font-semibold text-right ${
                TIER_TEXT[row.tier] || "text-[#111827]"
              }`}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const ScoreGauge = ({ score, maxScore, label, tier = "orange" }) => {
    const { stroke, text } = TIER_COLORS[tier];
    const radius = 140;
    const circumference = Math.PI * radius;
    const percentage = Math.min(Math.max(score / maxScore, 0), 1);
    const dashOffset = circumference * (1 - percentage);

    return (
      <div className="flex flex-col items-center w-full max-w-[360px]">
        <div className="relative w-full">
          <svg viewBox="0 0 300 185" className="w-full">
            <path
              d="M30 150 A120 120 0 0 1 270 150"
              fill="none"
              stroke="#EEF0F8"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <path
              d="M30 150 A120 120 0 0 1 270 150"
              fill="none"
              stroke={stroke}
              strokeWidth="20"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset .7s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
            <div className={`text-[56px] font-extrabold leading-none ${text}`}>
              {score}
            </div>
            <div className="mt-2 text-[16px] font-semibold text-[#8B8B99]">
              {t("report.autism_children.out_of")} {maxScore}
            </div>
          </div>
          <div className="absolute bottom-2 left-6 right-6 flex justify-between text-[#8B8B99] text-[12px] font-[700]">
            <span>{t("report.autism_children.low")}</span>
            <span>{t("report.autism_children.high")}</span>
          </div>
        </div>
        <h2 className={`mt-4 text-xl font-bold text-center ${text}`}>{label}</h2>
      </div>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => (
    <li>
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold text-gray-700 capitalize">{label}</span>
        <span className="text-sm" style={{ color }}>
          {percentage}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-5">
        <div
          className="h-5 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </li>
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <div className="w-full max-w-6xl mx-auto py-6">
              {/* SECTION 1 - Screening Results */}
              <section className="w-full bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="orange" />
                  }
                >
                  {isAuth && (
                    <div className="w-full px-8 lg:px-10 flex justify-end gap-[10px]">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}

                  <div className="pb-8">
                    <div className="pb-10 md:w-[83%]">
                      <p
                        className="text-[15.5px] leading-[1.75] font-normal text-[#4B5563]"
                        dangerouslySetInnerHTML={{
                          __html: contentData?.intro || "",
                        }}
                      />
                    </div>

                    <div className="w-full">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                        <div className="flex flex-col items-center">
                          <ScoreGauge
                            score={overAllScore}
                            maxScore={100}
                            label={
                              contentData?.overallResult?.bands?.[overAllLevel]
                                ?.label
                            }
                            tier="orange"
                          />

                          <div className="mt-6 w-full">
                            <ScoresTable
                              rows={[
                                {
                                  label: t(
                                    "report.psychopathy.trait_score_label",
                                  ),
                                  value: `${overAllScore} / 100`,
                                  tier: "orange",
                                },
                                {
                                  label: t("report.autism_children.overall_result"),
                                  value:
                                    contentData?.overallResult?.bands?.[
                                      overAllLevel
                                    ]?.shortLabel,
                                  tier: "orange",
                                },
                              ]}
                            />
                          </div>
                        </div>

                        <div className="relative rounded-2xl overflow-hidden h-[320px] md:h-[520px] bg-[#F5F5F5] flex items-center justify-center">
                          <img
                            src={result1Image}
                            alt="Assessment Result"
                            className="w-[92%] h-[92%] object-contain"
                          />
                        </div>
                      </div>

                      <h3 className="text-[18px] font-bold text-[#1F2937] mb-3 mt-6">
                        {contentData?.overallResult?.heading}
                      </h3>

                      <ResultMeaningCard
                        title={
                          contentData?.overallResult?.bands?.[overAllLevel]
                            ?.label
                        }
                        description={
                          contentData?.overallResult?.bands?.[overAllLevel]
                            ?.description
                        }
                        tier="orange"
                      />
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {/* SECTION 2 - How is this scored */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  heroBanner={
                    <HeroBanner
                      title={contentData?.howScored?.heading}
                      color="violet"
                    />
                  }
                >
                  <div className="px-4 py-4">
                    <p className="text-gray-700 text-[15.5px] leading-relaxed mb-6">
                      {contentData?.howScored?.content}
                    </p>

                    <div className="w-full max-w-md">
                      <ul className="mt-4">
                        {progressScore?.map((item) => (
                          <ProgressBar
                            key={item.label}
                            label={item.label}
                            percentage={item.percentage}
                            color={item.color}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {/* SECTION 3 - Understanding Results */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  heroBanner={
                    <HeroBanner
                      title={t("report.psychopathy.understanding_your_results")}
                      color="blue"
                    />
                  }
                >
                  <div className="px-4 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div
                        className="rounded-2xl p-6"
                        style={{ backgroundColor: "#fff4e6" }}
                      >
                        <h3 className="text-[16px] font-bold text-gray-900 mb-2">
                          {contentData?.factors?.sociopathy?.label}
                        </h3>
                        <p className="text-[#E0791F] font-semibold text-[14px] mb-2">
                          {
                            contentData?.factors?.sociopathy?.bands?.[
                              sociopathyScoreLevel
                            ]?.label
                          }
                        </p>
                        <p className="text-gray-600 text-[14px] leading-relaxed">
                          {
                            contentData?.factors?.sociopathy?.bands?.[
                              sociopathyScoreLevel
                            ]?.description
                          }
                        </p>
                      </div>

                      <div
                        className="rounded-2xl p-6"
                        style={{ backgroundColor: "#ffe6e6" }}
                      >
                        <h3 className="text-[16px] font-bold text-gray-900 mb-2">
                          {contentData?.factors?.impulsiveness?.label}
                        </h3>
                        <p className="text-red-500 font-semibold text-[14px] mb-2">
                          {
                            contentData?.factors?.impulsiveness?.bands?.[
                              impulsivityScoreLevel
                            ]?.label
                          }
                        </p>
                        <p className="text-gray-600 text-[14px] leading-relaxed">
                          {
                            contentData?.factors?.impulsiveness?.bands?.[
                              impulsivityScoreLevel
                            ]?.description
                          }
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 rounded-xl p-6">
                      <p className="text-lg font-semibold text-gray-800 mb-3">
                        {contentData?.personalizedBreakdown?.heading}
                      </p>
                      <ul className="list-disc pl-6 space-y-2">
                        {getYourInsightContent?.map((item, index) => (
                          <li key={index} className="text-gray-700 leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {/* SECTION 4 - Next Steps */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSectionV2
                  heroBanner={
                    <HeroBanner
                      title={contentData?.seekHelp?.heading}
                      color="green"
                    />
                  }
                >
                  <div className="px-4 py-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="flex flex-col gap-6">
                        <div className="bg-[#EAF6EE] border border-[#CFE9DA] rounded-2xl p-6">
                          <h3 className="text-[16px] font-bold text-[#2E7D5A] mb-3">
                            {contentData?.seekHelp?.heading}
                          </h3>
                          <p className="text-gray-700 text-[14.5px] leading-relaxed">
                            {contentData?.seekHelp?.content}
                          </p>
                          <p className="text-gray-700 text-[14.5px] leading-relaxed mt-3">
                            {contentData?.seekHelp?.addendums?.[overAllLevel]}
                          </p>
                        </div>
                      </div>

                      <div className="relative rounded-2xl overflow-hidden bg-[#F5F5F5] min-h-[320px] flex items-center justify-center">
                        <img
                          src={result2Image}
                          alt=""
                          className="absolute bottom-16 left-1/2 -translate-x-1/2 w-full p-8"
                        />
                      </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                      <h3 className="text-[16px] font-bold text-red-700 mb-3">
                        {contentData?.disclaimer?.heading}
                      </h3>
                      <p
                        className="text-gray-700 text-[14.5px] leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: contentData?.disclaimer?.content || "",
                        }}
                      />
                    </div>

                    <div className="mt-8">
                      <h2 className="mb-3 text-lg font-bold text-gray-900">
                        {contentData?.resources?.heading}
                      </h2>
                      <p className="mb-2 text-[15px] text-gray-700">
                        {contentData?.resources?.intro}
                      </p>
                      <ul className="space-y-3">
                        {contentData?.resources?.items?.map((item, i) => (
                          <li key={i} className="flex text-gray-700 text-[15px]">
                            <span className="mr-2 text-lg font-bold leading-6">
                              •
                            </span>
                            <span>
                              {item.label}
                              {item.source && (
                                <>
                                  {" — "}
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-indigo-600 hover:underline hover:text-indigo-700"
                                  >
                                    {item.source}
                                  </a>
                                </>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </ExpandableSectionV2>
              </section>

              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.CUSTOM_BLUE}
                />
              ) : (
                CallToActionSectionForUnAuth({ contentData })
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </div>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SociopathTestReport;
